import React, { useState, useEffect } from 'react';
import { BaseComponent, Building, PhotoModel } from '../../types/protocols';
import { BaseComponentImpl } from '../../types/implementations';
import { translate } from '../../types/EnumTranslations';
import { uploadImagesForComponent } from '../../Helpers/ImageUploadUtils';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../firebase';
import { extractExifData, extractExifDataAlternative, getBestExifDate } from '../../Helpers/ExifUtils';
import { getDatabase, ref as dbRef, push } from 'firebase/database';

interface ImageUploadModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSubmit: (photoModels: PhotoModel[]) => void;
    files: File[];
    component: BaseComponent;
    building: Building;
    buildingKey: string;
}

interface ImagePreview {
    id: string; // Add unique ID for stable keys
    file: File;
    preview: string;
    storagePath: string;
    photoModel: PhotoModel;
    exifData?: any;
    date: Date; // Add date as a separate field for the date picker
}

const ImageUploadModal: React.FC<ImageUploadModalProps> = ({
    isOpen,
    onClose,
    onSubmit,
    files,
    component,
    building,
    buildingKey
}) => {
    const [user] = useAuthState(auth);
    const [imagePreviews, setImagePreviews] = useState<ImagePreview[]>([]);
    const [isUploading, setIsUploading] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<{
        current: number;
        total: number;
        currentFileName: string;
    } | null>(null);

    // Function to format date for storage in YYYY-MM-DD hh:mm:ss format
    const formatDateForStorage = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        
        return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
    };

    // Function to format date for datetime-local input (YYYY-MM-DDTHH:mm)
    const formatDateForInput = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        
        return `${year}-${month}-${day}T${hours}:${minutes}`;
    };


    useEffect(() => {
        if (isOpen && files.length > 0) {
            const componentImpl = new BaseComponentImpl(component);
            const previews: ImagePreview[] = [];

            const processFiles = async () => {
                for (let index = 0; index < files.length; index++) {
                    const file = files[index];
                    
                    // Check for HEIC files and reject them
                    if (file.type === 'image/heic' || file.name.toLowerCase().endsWith('.heic')) {
                        alert('HEIC format is not supported. Please convert your image to JPEG or PNG format before uploading.');
                        continue; // Skip this file
                    }
                    
                    // Handle supported image formats
                    let preview: string;
                    let processedFile = file;
                    
                    if (file.type === 'image/png' || file.name.toLowerCase().endsWith('.png')) {
                        // PNG files are supported natively by browsers
                        preview = URL.createObjectURL(file);
                    } else {
                        preview = URL.createObjectURL(file);
                    }
                    
                    // Extract EXIF data
                    let exifData;
                    if (file.type === 'image/png' || file.name.toLowerCase().endsWith('.png')) {
                        // PNG files can have EXIF data, try both methods
                        exifData = await extractExifData(file);
                        if (!exifData || Object.keys(exifData).length === 0) {
                            exifData = await extractExifDataAlternative(file);
                        }
                    } else {
                        exifData = await extractExifData(file);
                    }
                    const exifDate = getBestExifDate(exifData);
                    const currentDate = new Date();
                    
                    // Handle EXIF date properly - EXIF dates are typically in local time
                    let dateForPicker: Date;
                    let finalDate: string;
                    
                    if (exifDate) {
                        // Parse the EXIF date string and treat it as local time
                        // EXIF dates are in format "YYYY:MM:DD HH:mm:ss" and are local time
                        const datePart = exifDate.split(' ')[0]; // "YYYY:MM:DD"
                        const timePart = exifDate.split(' ')[1]; // "HH:mm:ss"
                        const isoString = datePart.replace(/^(\d{4}):(\d{2}):(\d{2})/, '$1-$2-$3') + 'T' + timePart;
                        const exifDateObj = new Date(isoString);
                        dateForPicker = exifDateObj;
                        finalDate = formatDateForStorage(exifDateObj);
                    } else {
                        dateForPicker = currentDate;
                        finalDate = formatDateForStorage(currentDate);
                    }
                    
                    // Generate a temporary key using Firebase push
                    const database = getDatabase();
                    const photosRef = dbRef(database, `buildings/${buildingKey}/structure/components/${component.key}/photos`);
                    const newPhotoRef = push(photosRef);
                    const tempKey = newPhotoRef.key!;
                    
                    const storagePath = `buildings/${buildingKey}/structure/components/${component.key}/photos/${tempKey}`;

                    const photoModel: PhotoModel = {
                        key: tempKey,
                        author: user?.uid || 'unknown_user',
                        date: finalDate,
                        width: 0, // Will be set after image loads
                        height: 0, // Will be set after image loads
                        storagePath: storagePath
                    };

                    previews.push({
                        id: `preview-${Date.now()}-${index}`, // Unique ID for stable React keys
                        file: processedFile,
                        preview,
                        storagePath,
                        photoModel,
                        exifData,
                        date: dateForPicker
                    });
                }

                setImagePreviews(previews);
            };

            processFiles();
        }
    }, [isOpen, files, component, buildingKey, user]);

    useEffect(() => {
        return () => {
            // Clean up object URLs
            imagePreviews.forEach(preview => {
                URL.revokeObjectURL(preview.preview);
            });
        };
    }, [imagePreviews]);

    const handleImageLoad = (id: string, event: React.SyntheticEvent<HTMLImageElement>) => {
        const img = event.currentTarget;
        setImagePreviews(prev => prev.map(preview => 
            preview.id === id 
                ? {
                    ...preview,
                    photoModel: {
                        ...preview.photoModel,
                        width: img.naturalWidth,
                        height: img.naturalHeight
                    }
                }
                : preview
        ));
    };

    const handleSubmit = async () => {
        setIsUploading(true);
        setUploadProgress({
            current: 0,
            total: imagePreviews.length,
            currentFileName: ''
        });

        const uploadedPhotoModels: PhotoModel[] = [];
        let successCount = 0;
        let failureCount = 0;

        try {
            // Upload images sequentially
            for (let i = 0; i < imagePreviews.length; i++) {
                const preview = imagePreviews[i];
                
                setUploadProgress({
                    current: i + 1,
                    total: imagePreviews.length,
                    currentFileName: preview.file.name
                });

                try {
                    console.log(`Uploading image ${i + 1}/${imagePreviews.length}: ${preview.file.name}`);
                    
                    const uploadResult = await uploadImagesForComponent(
                        buildingKey,
                        component.key,
                        [preview.file],
                        user?.uid || 'unknown_user'
                    );
                    
                    if (uploadResult.success && uploadResult.photoModels) {
                        uploadedPhotoModels.push(...uploadResult.photoModels);
                        successCount++;
                        console.log(`Successfully uploaded: ${preview.file.name}`);
                    } else {
                        throw new Error(uploadResult.error || 'Upload failed');
                    }
                } catch (error) {
                    console.error(`Failed to upload ${preview.file.name}:`, error);
                    failureCount++;
                    // Continue with next image instead of stopping
                }

                // Small delay between uploads to prevent overwhelming the backend
                if (i < imagePreviews.length - 1) {
                    await new Promise(resolve => setTimeout(resolve, 500));
                }
            }

            // Show summary
            if (successCount > 0) {
                onSubmit(uploadedPhotoModels);
                
                if (failureCount > 0) {
                    alert(`${successCount} bilder laddades upp framgångsrikt. ${failureCount} bilder misslyckades.`);
                }
            } else {
                throw new Error('Alla uppladdningar misslyckades');
            }

        } catch (error) {
            console.error('Upload process failed:', error);
            alert(`Uppladdning misslyckades: ${error instanceof Error ? error.message : 'Okänt fel'}`);
        } finally {
            setIsUploading(false);
            setUploadProgress(null);
        }
    };

    const handleDateChange = (id: string, newDate: Date) => {
        setImagePreviews(prev => prev.map(preview => 
            preview.id === id 
                ? {
                    ...preview,
                    date: newDate,
                    photoModel: {
                        ...preview.photoModel,
                        date: formatDateForStorage(newDate)
                    }
                }
                : preview
        ));
    };

    const handleDeleteRow = (id: string) => {
        setImagePreviews(prev => {
            const previewToDelete = prev.find(p => p.id === id);
            if (previewToDelete) {
                // Clean up the object URL for the deleted image
                URL.revokeObjectURL(previewToDelete.preview);
            }
            return prev.filter(p => p.id !== id);
        });
    };

    // Drag and drop handlers for the modal
    const handleModalDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(true);
    };

    const handleModalDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        // Only set drag over to false if we're leaving the modal entirely
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setIsDragOver(false);
        }
    };

    const handleModalDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        const droppedFiles = Array.from(e.dataTransfer.files).filter(file => 
            file.type.startsWith('image/')
        );

        if (droppedFiles.length > 0) {
            // Process the new files and add them to existing previews
            const newPreviews: ImagePreview[] = [];
            const currentDate = new Date();

            for (let index = 0; index < droppedFiles.length; index++) {
                const file = droppedFiles[index];
                
                // Check for HEIC files and reject them
                if (file.type === 'image/heic' || file.name.toLowerCase().endsWith('.heic')) {
                    alert('HEIC format is not supported. Please convert your image to JPEG or PNG format before uploading.');
                    continue; // Skip this file
                }
                
                // Handle supported image formats
                let preview: string;
                let processedFile = file;
                
                if (file.type === 'image/png' || file.name.toLowerCase().endsWith('.png')) {
                    // PNG files are supported natively by browsers
                    preview = URL.createObjectURL(file);
                } else {
                    preview = URL.createObjectURL(file);
                }
                
                // Extract EXIF data
                let exifData;
                if (file.type === 'image/png' || file.name.toLowerCase().endsWith('.png')) {
                    // PNG files can have EXIF data, try both methods
                    exifData = await extractExifData(file);
                    if (!exifData || Object.keys(exifData).length === 0) {
                        exifData = await extractExifDataAlternative(file);
                    }
                } else {
                    exifData = await extractExifData(file);
                }
                const exifDate = getBestExifDate(exifData);
                
                // Handle EXIF date properly - EXIF dates are typically in local time
                let dateForPicker: Date;
                let finalDate: string;
                
                if (exifDate) {
                    // Parse the EXIF date string and treat it as local time
                    // EXIF dates are in format "YYYY:MM:DD HH:mm:ss" and are local time
                    const datePart = exifDate.split(' ')[0]; // "YYYY:MM:DD"
                    const timePart = exifDate.split(' ')[1]; // "HH:mm:ss"
                    const isoString = datePart.replace(/^(\d{4}):(\d{2}):(\d{2})/, '$1-$2-$3') + 'T' + timePart;
                    const exifDateObj = new Date(isoString);
                    dateForPicker = exifDateObj;
                    finalDate = formatDateForStorage(exifDateObj);
                } else {
                    dateForPicker = currentDate;
                    finalDate = formatDateForStorage(currentDate);
                }
                
                // Generate a temporary key using Firebase push
                const database = getDatabase();
                const photosRef = dbRef(database, `buildings/${buildingKey}/structure/components/${component.key}/photos`);
                const newPhotoRef = push(photosRef);
                const tempKey = newPhotoRef.key!;
                
                const storagePath = `buildings/${buildingKey}/structure/components/${component.key}/photos/${tempKey}`;

                const photoModel: PhotoModel = {
                    key: tempKey,
                    author: user?.uid || 'unknown_user',
                    date: finalDate,
                    width: 0, // Will be set after image loads
                    height: 0, // Will be set after image loads
                    storagePath: storagePath
                };

                newPreviews.push({
                    id: `preview-${Date.now()}-${index}-${Math.random()}`, // Unique ID for stable React keys
                    file: processedFile,
                    preview,
                    storagePath,
                    photoModel,
                    exifData,
                    date: dateForPicker
                });
            }

            // Add new previews to existing ones
            setImagePreviews(prev => [...prev, ...newPreviews]);
        }
    };

    if (!isOpen) return null;

    const componentImpl = new BaseComponentImpl(component);

    return (
        <div 
            style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000
            }}
            onClick={isUploading ? undefined : onClose}
        >
            <div 
                style={{
                    backgroundColor: isDragOver ? '#f0f8ff' : 'white',
                    borderRadius: '10px',
                    padding: '30px',
                    maxWidth: '90vw',
                    maxHeight: '90vh',
                    overflow: 'auto',
                    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
                    border: isDragOver ? '3px dashed #007bff' : '3px solid transparent',
                    transition: 'all 0.3s ease'
                }}
                onClick={(e) => e.stopPropagation()}
                onDragOver={handleModalDragOver}
                onDragLeave={handleModalDragLeave}
                onDrop={handleModalDrop}
            >
                <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '20px',
                    borderBottom: '1px solid #eee',
                    paddingBottom: '15px'
                }}>
                    <h2 style={{ margin: 0, color: '#333' }}>
                        Ladda upp bilder - {componentImpl.displayName(building)}
                    </h2>
                    <button
                        onClick={isUploading ? undefined : onClose}
                        disabled={isUploading}
                        style={{
                            background: 'none',
                            border: 'none',
                            fontSize: '24px',
                            cursor: isUploading ? 'not-allowed' : 'pointer',
                            color: isUploading ? '#ccc' : '#666'
                        }}
                    >
                        ×
                    </button>
                </div>

                <div style={{ marginBottom: '20px' }}>
                    <p style={{ margin: '0 0 10px 0', color: '#666' }}>
                        <strong>Komponent:</strong> {componentImpl.displayName(building)}
                    </p>
                    <p style={{ margin: '0 0 10px 0', color: '#666' }}>
                        <strong>Typ:</strong> {translate(component.type)}
                    </p>
                    {component.cardinalDirection?.value && (
                        <p style={{ margin: '0 0 10px 0', color: '#666' }}>
                            <strong>Riktning:</strong> {translate(component.cardinalDirection.value)}
                        </p>
                    )}
                </div>

                {/* Drag and drop indicator */}
                {isDragOver && (
                    <div style={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        backgroundColor: 'rgba(0, 123, 255, 0.9)',
                        color: 'white',
                        padding: '20px 40px',
                        borderRadius: '10px',
                        fontSize: '18px',
                        fontWeight: 'bold',
                        textAlign: 'center',
                        zIndex: 1001,
                        pointerEvents: 'none',
                        boxShadow: '0 4px 20px rgba(0, 123, 255, 0.3)'
                    }}>
                        📁 Släpp bilder här för att lägga till dem
                    </div>
                )}

                {!isUploading && (
                    <div style={{ marginBottom: '30px' }}>
                        {imagePreviews.map((preview, index) => (
                        <div key={preview.id} style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '15px',
                            padding: '15px',
                            border: '1px solid #ddd',
                            borderRadius: '8px',
                            marginBottom: '10px',
                            backgroundColor: '#fafafa'
                        }}>
                            {/* Thumbnail */}
                            <div style={{ flexShrink: 0 }}>
                                <img
                                    src={preview.preview}
                                    alt={`Preview ${index + 1}`}
                                    style={{
                                        width: '80px',
                                        height: '80px',
                                        objectFit: 'cover',
                                        borderRadius: '5px'
                                    }}
                                    onLoad={(e) => handleImageLoad(preview.id, e)}
                                />
                            </div>

                            {/* File info */}
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ marginBottom: '5px' }}>
                                    <strong style={{ fontSize: '14px' }}>{preview.file.name}</strong>
                                </div>
                                <div style={{ fontSize: '12px', color: '#666' }}>
                                    {(preview.file.size / 1024 / 1024).toFixed(2)} MB
                                    {preview.photoModel.width > 0 && preview.photoModel.height > 0 && (
                                        <span> • {preview.photoModel.width} × {preview.photoModel.height} px</span>
                                    )}
                                </div>
                            </div>

                            {/* Date picker */}
                            <div style={{ flexShrink: 0 }}>
                                <label style={{ display: 'block', marginBottom: '5px', fontSize: '12px', fontWeight: 'bold' }}>
                                    Datum:
                                </label>
                                <input
                                    type="datetime-local"
                                    value={formatDateForInput(preview.date)}
                                    onChange={(e) => handleDateChange(preview.id, new Date(e.target.value))}
                                    disabled={isUploading}
                                    style={{
                                        padding: '8px',
                                        border: '1px solid #ccc',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        minWidth: '180px',
                                        backgroundColor: isUploading ? '#f5f5f5' : 'white',
                                        cursor: isUploading ? 'not-allowed' : 'text'
                                    }}
                                />
                                {preview.exifData && (preview.exifData.dateTimeOriginal || preview.exifData.dateTimeDigitized || preview.exifData.dateTime) && (
                                    <div style={{ fontSize: '10px', color: '#28a745', marginTop: '2px' }}>
                                        (från EXIF)
                                    </div>
                                )}
                            </div>

                            {/* Delete button */}
                            <div style={{ flexShrink: 0 }}>
                                <button
                                    onClick={() => handleDeleteRow(preview.id)}
                                    disabled={isUploading}
                                    style={{
                                        background: isUploading ? '#ccc' : '#dc3545',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '4px',
                                        padding: '8px 12px',
                                        cursor: isUploading ? 'not-allowed' : 'pointer',
                                        fontSize: '12px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '5px'
                                    }}
                                >
                                    🗑️ Ta bort
                                </button>
                            </div>
                        </div>
                    ))}
                    </div>
                )}

                {imagePreviews.length > 0 ? (
                    <div>
                        {/* Progress Bar */}
                        {uploadProgress && (
                            <div style={{
                                marginBottom: '20px',
                                padding: '15px',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '8px',
                                border: '1px solid #e9ecef'
                            }}>
                                <div style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    marginBottom: '10px'
                                }}>
                                    <span style={{ fontWeight: 'bold', color: '#333' }}>
                                        Laddar upp bilder...
                                    </span>
                                    <span style={{ color: '#666', fontSize: '14px' }}>
                                        {uploadProgress.current} av {uploadProgress.total}
                                    </span>
                                </div>
                                
                                <div style={{
                                    width: '100%',
                                    height: '8px',
                                    backgroundColor: '#e9ecef',
                                    borderRadius: '4px',
                                    overflow: 'hidden',
                                    marginBottom: '8px'
                                }}>
                                    <div style={{
                                        width: `${(uploadProgress.current / uploadProgress.total) * 100}%`,
                                        height: '100%',
                                        backgroundColor: '#007bff',
                                        transition: 'width 0.3s ease'
                                    }} />
                                </div>
                                
                                <div style={{ fontSize: '12px', color: '#666' }}>
                                    {uploadProgress.currentFileName}
                                </div>
                            </div>
                        )}

                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '10px',
                            borderTop: '1px solid #eee',
                            paddingTop: '20px'
                        }}>
                        <button
                            onClick={onClose}
                            disabled={isUploading}
                            style={{
                                padding: '10px 20px',
                                border: '1px solid #ccc',
                                borderRadius: '5px',
                                backgroundColor: 'white',
                                cursor: isUploading ? 'not-allowed' : 'pointer',
                                color: '#666'
                            }}
                        >
                            Avbryt
                        </button>
                        <button
                            onClick={handleSubmit}
                            disabled={isUploading}
                            style={{
                                padding: '10px 20px',
                                border: 'none',
                                borderRadius: '5px',
                                backgroundColor: isUploading ? '#ccc' : '#007bff',
                                color: 'white',
                                cursor: isUploading ? 'not-allowed' : 'pointer'
                            }}
                        >
                            {isUploading ? 'Laddar upp...' : `Ladda upp ${imagePreviews.length} bilder`}
                        </button>
                        </div>
                    </div>
                ) : (
                    <div style={{
                        textAlign: 'center',
                        padding: '40px',
                        color: '#666',
                        fontSize: '16px',
                        borderTop: '1px solid #eee',
                        paddingTop: '20px'
                    }}>
                        Inga bilder att ladda upp
                    </div>
                )}
            </div>
        </div>
    );
};

export default ImageUploadModal;
